import { forwardRef } from 'react';

import { cva } from 'cva';
import { Input } from 'react-aria-components/Input';
import {
  NumberField as RACNumberField,
  type NumberFieldProps as RACNumberFieldProps,
  type ValidationResult,
} from 'react-aria-components/NumberField';

import { FieldDescription } from '../form/field_description';
import { FieldError } from '../form/field_error';
import { FieldLabel } from '../form/field_label';

import styles from './number_field.module.css';

export const numberFieldVariants = cva({
  base: styles.number_field,
  variants: {
    size: {
      s: styles.size_s,
      m: styles.size_m,
      l: styles.size_l,
    },
    readOnly: {
      true: styles.read_only,
    },
  },
  defaultVariants: {
    size: 'm',
  },
});

export type NumberFieldProps = RACNumberFieldProps & {
  description?: string;
  errorMessage?: string | ((validation: ValidationResult) => string);
  label?: string;
  placeholder?: string;
  size?: 's' | 'm' | 'l';
  tooltip?: React.ReactNode;
};

/**
 * Wrapper for NumberField component.
 *
 * Specify a minimum and maximum value.
 * Use `label` to provide a label for the field.
 * Use `placeholder` to provide a placeholder for the field.
 * Use `size` to specify the size of the field.
 *
 * See also https://react-spectrum.adobe.com/react-aria/NumberField.html
 *
 */

export const NumberField = forwardRef<HTMLInputElement, NumberFieldProps>(
  (
    {
      className,
      description,
      errorMessage,
      isReadOnly,
      isRequired,
      label,
      placeholder,
      size,
      tooltip,
      ...rest
    },
    ref,
  ) => {
    const classes = numberFieldVariants({
      size,
      readOnly: isReadOnly,
      className,
    });

    return (
      <RACNumberField
        ref={ref}
        isRequired={isRequired}
        isReadOnly={isReadOnly}
        {...rest}
        className={classes}
      >
        {label && (
          <FieldLabel isRequired={isRequired} tooltip={tooltip}>
            {label}
          </FieldLabel>
        )}
        <Input placeholder={placeholder} />
        {description && <FieldDescription>{description}</FieldDescription>}
        <FieldError>{errorMessage}</FieldError>
      </RACNumberField>
    );
  },
);
